import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FaWhatsapp, FaTimes, FaCheckCircle, FaCopy, FaCheck } from "react-icons/fa";

const SLICE_COLORS = [
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#3B82F6", // Blue
  "#10B981", // Emerald
  "#F59E0B", // Amber
  "#EC4899", // Pink
];

const DEFAULT_SLICES = [
  "10% OFF",
  "Free Gift",
  "₹50 Off",
  "5% OFF",
  "Special Deal",
  "Surprise Gift",
];

const LeadCaptureModal = ({ company, API_DOMAIN, subdomain, hasCart = false }) => {
  const [show, setShow] = useState(false);
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState("spin"); // "spin" | "scratch" | "popup" | "form" | "success"
  const [winningOffer, setWinningOffer] = useState("");
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);

  // Scratch card canvas state
  const canvasRef = useRef(null);
  const isScratching = useRef(false);
  const [isScratched, setIsScratched] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);

  const companyId = company?.company_id || subdomain || "COMP-000001";
  const storageKey = `zen_lead_dismissed_${companyId}`;
  const claimedStorageKey = `zen_lead_claimed_${companyId}`;

  // Bulletproof detection across both subdomain and company_id
  const checkIsClaimed = () => {
    try {
      if (localStorage.getItem("zen_store_lead_claimed") === "true") return true;
      const keys = [
        subdomain ? `zen_lead_claimed_${subdomain}` : null,
        companyId ? `zen_lead_claimed_${companyId}` : null,
        company?.company_id ? `zen_lead_claimed_${company.company_id}` : null,
        company?.company_code ? `zen_lead_claimed_${company.company_code}` : null,
      ].filter(Boolean);

      if (keys.some((k) => localStorage.getItem(k) === "true")) {
        return true;
      }

      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith("zen_lead_claimed_") && localStorage.getItem(k) === "true") {
          return true;
        }
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  const [isClaimed, setIsClaimed] = useState(checkIsClaimed);

  // Sync state whenever company finishes loading
  useEffect(() => {
    if (checkIsClaimed()) {
      setIsClaimed(true);
    }
  }, [company, companyId, subdomain]);

  const markAllKeysAsClaimed = () => {
    setIsClaimed(true);
    try {
      localStorage.setItem("zen_store_lead_claimed", "true");
      if (subdomain) localStorage.setItem(`zen_lead_claimed_${subdomain}`, "true");
      if (companyId) localStorage.setItem(`zen_lead_claimed_${companyId}`, "true");
      if (company?.company_id) localStorage.setItem(`zen_lead_claimed_${company.company_id}`, "true");
      if (company?.company_code) localStorage.setItem(`zen_lead_claimed_${company.company_code}`, "true");
      sessionStorage.setItem(storageKey, "true");
    } catch (e) {}
  };

  // 1. Fetch Admin Lead Magnet & Spinner Settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!API_DOMAIN) return;
      try {
        const res = await fetch(`${API_DOMAIN}/leads.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "get_lead_magnet_settings",
            company_id: companyId,
          }),
        });
        const data = await res.json();
        if (data.head && data.head.code === 200 && data.body) {
          const mType = data.body.magnet_type || "spinner";
          setSettings(data.body);
          if (mType === "scratch") {
            setStep("scratch");
          } else if (mType === "popup") {
            setStep("popup");
          } else {
            setStep("spin");
          }
        }
      } catch (err) {
        console.warn("Could not load lead magnet settings:", err);
      }
    };
    fetchSettings();
  }, [API_DOMAIN, companyId]);

  // 2. Auto-trigger modal after 6 seconds of browsing (only if never claimed)
  useEffect(() => {
    if (settings && settings.is_enabled === false) {
      return;
    }

    if (isClaimed) return;

    const dismissed = sessionStorage.getItem(storageKey);
    if (dismissed) return;

    const timer = setTimeout(() => {
      setShow(true);
    }, 6000);

    return () => clearTimeout(timer);
  }, [settings, storageKey, isClaimed]);

  const magnetType = settings?.magnet_type || "spinner";
  const isSpinner = magnetType === "spinner";
  const isScratch = magnetType === "scratch";
  const isPopup = magnetType === "popup";

  const slices = (settings?.slices && settings.slices.length >= 4) ? settings.slices : DEFAULT_SLICES;
  const numSlices = slices.length;
  const sliceAngle = 360 / numSlices;

  const rawHeadline = (settings?.headline || "").trim();
  const hasWheelKeywords =
    rawHeadline.toLowerCase().includes("wheel") ||
    rawHeadline.toLowerCase().includes("spin the") ||
    rawHeadline.toLowerCase().includes("lucky wheel");

  let headline = rawHeadline;
  if (isPopup) {
    if (!headline || hasWheelKeywords) {
      headline = "Special Welcome Gift For You!";
    }
  } else if (isScratch) {
    if (!headline || hasWheelKeywords) {
      headline = "Scratch Card To Reveal Exclusive Deal!";
    }
  } else {
    if (!headline) {
      headline = "Spin The Lucky Wheel & Win Exclusive Offers!";
    }
  }

  const couponCode = settings?.coupon_code || "WELCOME10";
  const primaryOffer = settings?.offer_value || slices[0] || "10% OFF";

  // Initialize Scratch Canvas
  useEffect(() => {
    if (show && isScratch && step === "scratch" && !isScratched) {
      const timer = setTimeout(() => {
        initScratchCanvas();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [show, isScratch, step, isScratched]);

  const initScratchCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;

    // Metallic Silver Gradient
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, "#CBD5E1");
    grad.addColorStop(0.25, "#94A3B8");
    grad.addColorStop(0.5, "#F1F5F9");
    grad.addColorStop(0.75, "#94A3B8");
    grad.addColorStop(1, "#CBD5E1");

    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Decorative text on scratch layer
    ctx.fillStyle = "#334155";
    ctx.font = "bold 13px 'Segoe UI', Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("✨ SCRATCH WITH FINGER OR MOUSE ✨", width / 2, height / 2 - 10);

    ctx.font = "600 11px 'Segoe UI', Arial, sans-serif";
    ctx.fillStyle = "#475569";
    ctx.fillText("To Reveal Your Secret Discount!", width / 2, height / 2 + 12);
  };

  const handleScratchMove = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || isScratched) return;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * canvas.width;
    const y = ((clientY - rect.top) / rect.height) * canvas.height;

    const ctx = canvas.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 22, 0, Math.PI * 2);
    ctx.fill();

    checkScratchProgress();
  };

  const checkScratchProgress = () => {
    const canvas = canvasRef.current;
    if (!canvas || isScratched) return;
    const ctx = canvas.getContext("2d");
    try {
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;
      let cleared = 0;
      const step = 24;
      let total = 0;
      for (let i = 3; i < data.length; i += 4 * step) {
        total++;
        if (data[i] < 128) {
          cleared++;
        }
      }
      const percent = (cleared / total) * 100;
      if (percent >= 30) {
        triggerScratchReveal();
      }
    } catch (e) {}
  };

  const triggerScratchReveal = () => {
    setIsScratched(true);
    setWinningOffer(primaryOffer);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setTimeout(() => {
      setStep("form");
    }, 1200);
  };

  const handleClose = () => {
    setShow(false);
    if (step === "success") {
      markAllKeysAsClaimed();
    }
    try {
      sessionStorage.setItem(storageKey, "true");
    } catch (e) {}
  };

  // Spin the Wheel Animation
  const handleSpin = () => {
    if (isSpinning) return;
    setIsSpinning(true);
    setErrorMsg("");

    let targetIndex = slices.findIndex(
      (s) => s.toLowerCase().includes("10") || s.toLowerCase().includes("off")
    );
    if (targetIndex === -1) targetIndex = 0;

    const extraTurns = 5 * 360;
    const targetSliceCenter = targetIndex * sliceAngle + sliceAngle / 2;
    const finalAngle = extraTurns + (360 - targetSliceCenter) + 270;

    setRotation(finalAngle);

    setTimeout(() => {
      setIsSpinning(false);
      const won = slices[targetIndex] || primaryOffer;
      setWinningOffer(won);
      setStep("form");
    }, 4200);
  };

  // Submit Lead with phone and name
  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanPhone = String(phone || "").trim().replace(/\D/g, "");
    if (cleanPhone.length < 10) {
      setErrorMsg("Please enter a valid 10-digit mobile number");
      return;
    }
    setErrorMsg("");
    setLoading(true);

    const claimed = winningOffer || primaryOffer || "Special Offer";
    const leadSource = isSpinner ? "spinner" : (isScratch ? "scratch_card" : "welcome_gift");

    const payload = {
      action: "create_lead",
      company_id: companyId,
      customer_name: name.trim() || "Store Visitor",
      customer_phone: cleanPhone,
      lead_source: leadSource,
      offer_claimed: claimed,
    };

    try {
      const res = await fetch(`${API_DOMAIN}/leads.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.head && data.head.code === 200) {
        setStep("success");
        markAllKeysAsClaimed();
      } else {
        setErrorMsg(data.head?.msg || "Failed to claim offer. Try again.");
      }
    } catch (err) {
      setStep("success");
      setIsClaimed(true);
      try {
        localStorage.setItem(claimedStorageKey, "true");
        sessionStorage.setItem(storageKey, "true");
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCoupon = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(couponCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleWhatsAppRedirect = () => {
    markAllKeysAsClaimed();
    const rawShopPhone = company?.phone || company?.mobile || "";
    const cleanShopPhone = String(rawShopPhone).replace(/\D/g, "");
    const formattedShopPhone = cleanShopPhone.length === 10 ? `91${cleanShopPhone}` : cleanShopPhone;
    const wonDesc = winningOffer || primaryOffer;
    const msg = `Hello ${company?.company_name || "Store"}! I just unlocked ${wonDesc} on your store (Coupon: ${couponCode}). Please share deals with me!`;
    const url = formattedShopPhone
      ? `https://wa.me/${formattedShopPhone}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    handleClose();
  };

  if (settings && settings.is_enabled === false) {
    return null;
  }

  // Once claimed, if modal is closed, do NOT render floating button or anything
  if (isClaimed && !show) {
    return null;
  }

  // Floating Button configuration based on format
  const fabText = isSpinner
    ? "Spin & Win Offer!"
    : isScratch
    ? "Scratch & Win!"
    : "Claim Welcome Gift!";

  return (
    <>
      <style>
        {`
          @keyframes spinFabPulse {
            0%, 100% {
              transform: translateY(0) scale(1);
              box-shadow: 0 6px 20px rgba(255, 91, 82, 0.45), 0 2px 6px rgba(0, 0, 0, 0.08);
            }
            50% {
              transform: translateY(-2.5px) scale(1.02);
              box-shadow: 0 10px 24px rgba(255, 91, 82, 0.58), 0 3px 8px rgba(0, 0, 0, 0.12);
            }
          }
          @media (max-width: 576px) {
            .spin-and-win-fab {
              padding: 9px 18px !important;
              font-size: 13.5px !important;
              left: 14px !important;
            }
          }
        `}
      </style>

      {/* 🎡 Floating Pill Button matching user screenshot */}
      {!show && !isClaimed && (
        <div
          role="button"
          tabIndex={0}
          aria-label={fabText}
          onClick={() => {
            if (step === "success") {
              setStep(isScratch ? "scratch" : (isPopup ? "popup" : "spin"));
              setIsScratched(false);
            }
            setShow(true);
          }}
          className="spin-and-win-fab"
          style={{
            position: "fixed",
            bottom: hasCart ? "88px" : "24px",
            left: "20px",
            zIndex: 1040,
            background: "linear-gradient(90deg, #FF5B52 0%, #FF8A48 100%)",
            color: "#FFFFFF",
            padding: "10px 22px",
            borderRadius: "50px",
            boxShadow: "0 6px 20px rgba(255, 91, 82, 0.45), 0 2px 6px rgba(0, 0, 0, 0.08)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "9px",
            fontWeight: "800",
            fontSize: "14.5px",
            letterSpacing: "0.2px",
            fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
            userSelect: "none",
            border: "1.5px solid rgba(255, 255, 255, 0.25)",
            backdropFilter: "blur(4px)",
            transition: "all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            animation: "spinFabPulse 2.8s infinite ease-in-out",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px) scale(1.03)";
            e.currentTarget.style.boxShadow = "0 10px 26px rgba(255, 91, 82, 0.6), 0 3px 8px rgba(0, 0, 0, 0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0) scale(1)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 91, 82, 0.45), 0 2px 6px rgba(0, 0, 0, 0.08)";
          }}
        >
          {/* Icon */}
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {isSpinner ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 12L7 21.5M12 12L17 21.5M8.5 18.5H15.5" stroke="#FFE8A3" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="10" r="7.5" stroke="#FFFFFF" strokeWidth="1.9" fill="rgba(255,255,255,0.08)" />
                <circle cx="12" cy="10" r="2.2" fill="#FFE8A3" stroke="#FFFFFF" strokeWidth="1" />
                <path d="M12 2.5V17.5M4.5 10H19.5M6.7 4.7L17.3 15.3M6.7 15.3L17.3 4.7" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" opacity="0.85" />
                <circle cx="12" cy="2.5" r="1.6" fill="#FFE8A3" />
                <circle cx="12" cy="17.5" r="1.6" fill="#FF6B6B" />
                <circle cx="4.5" cy="10" r="1.6" fill="#4D96FF" />
                <circle cx="19.5" cy="10" r="1.6" fill="#9B51E0" />
                <circle cx="6.7" cy="4.7" r="1.5" fill="#6BCB77" />
                <circle cx="17.3" cy="15.3" r="1.5" fill="#FFE8A3" />
                <circle cx="6.7" cy="15.3" r="1.5" fill="#FF6B6B" />
                <circle cx="17.3" cy="4.7" r="1.5" fill="#6BCB77" />
              </svg>
            ) : isScratch ? (
              <span style={{ fontSize: "18px", lineHeight: "1" }}>✨</span>
            ) : (
              <span style={{ fontSize: "18px", lineHeight: "1" }}>🎁</span>
            )}
          </span>

          {/* Text */}
          <span style={{ textShadow: "0 1px 2px rgba(0,0,0,0.18)", whiteSpace: "nowrap" }}>
            {fabText}
          </span>
        </div>
      )}

      {/* 🌟 Lead Capture Modal */}
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false}>
        <div
          style={{
            position: "relative",
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
            borderRadius: "18px",
            overflow: "hidden",
            boxShadow: "0 20px 45px rgba(0,0,0,0.2)",
            border: "none",
          }}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            aria-label="Close"
            style={{
              position: "absolute",
              top: "14px",
              right: "14px",
              zIndex: 10,
              background: "#F3F4F6",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#4B5563",
              transition: "all 0.2s",
            }}
          >
            <FaTimes size={14} />
          </button>

          {/* Modal Header */}
          <div
            style={{
              background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
              padding: "20px 24px 16px 24px",
              color: "#fff",
              textAlign: "center",
            }}
          >
            <span
              style={{
                display: "inline-block",
                background: "rgba(255,255,255,0.15)",
                padding: "3px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "1px",
                textTransform: "uppercase",
                marginBottom: "6px",
              }}
            >
              {isSpinner ? "🎡 Lucky Spin Wheel" : isScratch ? "✨ Lucky Scratch Card" : "🎁 Exclusive Welcome Gift"}
            </span>
            <h5 style={{ margin: 0, fontWeight: "800", fontSize: "16px", color: "#F8FAFC" }}>
              {headline}
            </h5>
          </div>

          <div style={{ padding: "20px 24px 24px 24px" }}>
            {/* FORMAT 1: SPINNER WHEEL */}
            {isSpinner && step === "spin" && (
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    position: "relative",
                    width: "220px",
                    height: "220px",
                    margin: "0 auto 18px auto",
                  }}
                >
                  {/* Pointer Needle */}
                  <div
                    style={{
                      position: "absolute",
                      top: "-8px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "0",
                      height: "0",
                      borderLeft: "10px solid transparent",
                      borderRight: "10px solid transparent",
                      borderTop: "20px solid #EF4444",
                      zIndex: 10,
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                    }}
                  />

                  {/* SVG Wheel */}
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      transition: isSpinning ? "transform 4s cubic-bezier(0.15, 0.9, 0.2, 1)" : "none",
                      transform: `rotate(${rotation}deg)`,
                      borderRadius: "50%",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
                    }}
                  >
                    <svg width="220" height="220" viewBox="0 0 200 200">
                      {slices.map((slice, i) => {
                        const startAngle = i * sliceAngle;
                        const endAngle = startAngle + sliceAngle;
                        const x1 = 100 + 100 * Math.cos((Math.PI * startAngle) / 180);
                        const y1 = 100 + 100 * Math.sin((Math.PI * startAngle) / 180);
                        const x2 = 100 + 100 * Math.cos((Math.PI * endAngle) / 180);
                        const y2 = 100 + 100 * Math.sin((Math.PI * endAngle) / 180);
                        const largeArc = sliceAngle > 180 ? 1 : 0;
                        const pathData = `M 100 100 L ${x1} ${y1} A 100 100 0 ${largeArc} 1 ${x2} ${y2} Z`;
                        const midAngle = startAngle + sliceAngle / 2;

                        return (
                          <g key={i}>
                            <path d={pathData} fill={SLICE_COLORS[i % SLICE_COLORS.length]} stroke="#fff" strokeWidth="1.5" />
                            <text
                              x="100"
                              y="30"
                              fill="#fff"
                              fontSize="9.5"
                              fontWeight="bold"
                              textAnchor="middle"
                              transform={`rotate(${midAngle}, 100, 100)`}
                            >
                              {slice}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>

                  {/* Center Knob */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "44px",
                      height: "44px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                      border: "3px solid #fff",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "11px",
                      zIndex: 5,
                    }}
                  >
                    ★
                  </div>
                </div>

                <Button
                  onClick={handleSpin}
                  disabled={isSpinning}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    border: "none",
                    fontWeight: "800",
                    fontSize: "15px",
                    boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                  }}
                >
                  {isSpinning ? "🎡 Spinning..." : "🎡 SPIN NOW!"}
                </Button>
              </div>
            )}

            {/* FORMAT 2: SCRATCH CARD */}
            {isScratch && step === "scratch" && (
              <div style={{ textAlign: "center" }}>
                <p style={{ fontSize: "12.5px", color: "#4B5563", marginBottom: "12px", fontWeight: "600" }}>
                  👆 Scratch the card below with your finger or mouse to reveal your prize!
                </p>

                {/* Scratch Canvas Card Container */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "280px",
                    height: "135px",
                    margin: "0 auto 14px auto",
                    borderRadius: "14px",
                    overflow: "hidden",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                    userSelect: "none",
                  }}
                >
                  {/* Underneath Revealed Offer */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 50%, #F59E0B 100%)",
                      border: "2.5px dashed #D97706",
                      borderRadius: "14px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "10px",
                    }}
                  >
                    <span style={{ fontSize: "11px", fontWeight: "800", color: "#92400E", textTransform: "uppercase" }}>
                      🎉 Congratulations! You Won:
                    </span>
                    <h3 style={{ fontSize: "26px", fontWeight: "900", color: "#B45309", margin: "4px 0" }}>
                      {primaryOffer}
                    </h3>
                    <span style={{ fontSize: "11.5px", color: "#78350F", fontWeight: "700", background: "rgba(255,255,255,0.75)", padding: "2px 10px", borderRadius: "10px" }}>
                      Coupon: {couponCode}
                    </span>
                  </div>

                  {/* Top Scratchable Canvas */}
                  <canvas
                    ref={canvasRef}
                    width={280}
                    height={135}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "14px",
                      cursor: "grab",
                      touchAction: "none",
                    }}
                    onMouseDown={(e) => {
                      isScratching.current = true;
                      handleScratchMove(e.clientX, e.clientY);
                    }}
                    onMouseMove={(e) => {
                      if (isScratching.current) {
                        handleScratchMove(e.clientX, e.clientY);
                      }
                    }}
                    onMouseUp={() => { isScratching.current = false; }}
                    onMouseLeave={() => { isScratching.current = false; }}
                    onTouchStart={(e) => {
                      isScratching.current = true;
                      if (e.touches[0]) {
                        handleScratchMove(e.touches[0].clientX, e.touches[0].clientY);
                      }
                    }}
                    onTouchMove={(e) => {
                      if (isScratching.current && e.touches[0]) {
                        handleScratchMove(e.touches[0].clientX, e.touches[0].clientY);
                      }
                    }}
                    onTouchEnd={() => { isScratching.current = false; }}
                  />
                </div>

                {/* Instant Reveal Button */}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={triggerScratchReveal}
                  style={{ borderRadius: "20px", fontSize: "12px", fontWeight: "700", padding: "5px 16px" }}
                >
                  ✨ Scratch All / Reveal Now
                </Button>
              </div>
            )}

            {/* FORMAT 3: WELCOME GIFT (POPUP) */}
            {isPopup && step === "popup" && (
              <div style={{ textAlign: "center" }}>
                {/* 3D Gift Box Icon */}
                <div
                  style={{
                    width: "68px",
                    height: "68px",
                    margin: "0 auto 12px auto",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 6px 18px rgba(245, 158, 11, 0.25)",
                    border: "3px solid #FFF",
                    fontSize: "32px",
                  }}
                >
                  🎁
                </div>

                {/* Offer Pill */}
                <div
                  style={{
                    display: "inline-block",
                    background: "linear-gradient(90deg, #EF4444 0%, #F59E0B 100%)",
                    color: "#FFF",
                    padding: "6px 20px",
                    borderRadius: "30px",
                    fontWeight: "900",
                    fontSize: "20px",
                    letterSpacing: "0.5px",
                    boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
                    marginBottom: "12px",
                  }}
                >
                  {primaryOffer}
                </div>

                <p style={{ fontSize: "12.5px", color: "#4B5563", marginBottom: "14px", lineHeight: "1.4" }}>
                  Welcome to our store! Enter your mobile number below to claim your exclusive gift coupon.
                </p>

                {/* Lead Form directly inside Welcome Gift */}
                <Form onSubmit={handleSubmit} style={{ textAlign: "left" }}>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontSize: "12px", fontWeight: "700", color: "#374151" }}>
                      Your Name (Optional)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. Ramesh"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={{ borderRadius: "8px", padding: "8px 12px", fontSize: "13.5px" }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: "12px", fontWeight: "700", color: "#374151" }}>
                      WhatsApp Mobile Number <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="10-digit mobile number"
                      maxLength={10}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      required
                      style={{ borderRadius: "8px", padding: "8px 12px", fontSize: "13.5px" }}
                    />
                  </Form.Group>

                  {errorMsg && (
                    <div style={{ color: "#EF4444", fontSize: "12px", marginBottom: "12px", fontWeight: "600" }}>
                      {errorMsg}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%",
                      padding: "11px",
                      borderRadius: "10px",
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      border: "none",
                      fontWeight: "800",
                      fontSize: "14px",
                      boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                    }}
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : "🎁 Claim Welcome Gift on WhatsApp"}
                  </Button>
                </Form>
              </div>
            )}

            {/* STEP: CLAIM FORM FOR SPINNER & SCRATCH AFTER WINNING */}
            {step === "form" && (
              <Form onSubmit={handleSubmit}>
                {winningOffer && (
                  <div
                    style={{
                      background: "#F0FDF4",
                      border: "1.5px dashed #22C55E",
                      borderRadius: "10px",
                      padding: "10px 14px",
                      marginBottom: "14px",
                      textAlign: "center",
                    }}
                  >
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#166534", textTransform: "uppercase" }}>
                      🎉 Congratulations! You Won:
                    </span>
                    <h4 style={{ fontWeight: "900", color: "#15803D", margin: "4px 0 0 0" }}>
                      {winningOffer}
                    </h4>
                  </div>
                )}

                <p style={{ fontSize: "12.5px", color: "#4B5563", marginBottom: "14px" }}>
                  Enter your mobile number to claim your exclusive discount code:
                </p>

                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: "12px", fontWeight: "700", color: "#374151" }}>
                    Your Name (Optional)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Ramesh"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{ borderRadius: "8px", padding: "9px 12px", fontSize: "14px" }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "12px", fontWeight: "700", color: "#374151" }}>
                    WhatsApp Mobile Number <span style={{ color: "red" }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    required
                    style={{ borderRadius: "8px", padding: "9px 12px", fontSize: "14px" }}
                  />
                </Form.Group>

                {errorMsg && (
                  <div style={{ color: "#EF4444", fontSize: "12px", marginBottom: "12px", fontWeight: "600" }}>
                    {errorMsg}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    border: "none",
                    fontWeight: "800",
                    fontSize: "15px",
                    boxShadow: "0 4px 14px rgba(16,185,129,0.35)",
                  }}
                >
                  {loading ? <Spinner animation="border" size="sm" /> : "Claim Coupon on WhatsApp 🎉"}
                </Button>
              </Form>
            )}

            {/* STEP: SUCCESS & COUPON CODE */}
            {step === "success" && (
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <FaCheckCircle size={44} color="#10B981" style={{ marginBottom: "10px" }} />
                <h5 style={{ fontWeight: "800", color: "#111827", marginBottom: "6px" }}>
                  Offer Claimed Successfully! 🎉
                </h5>
                <p style={{ fontSize: "12.5px", color: "#4B5563", marginBottom: "14px" }}>
                  Use this coupon code at checkout to enjoy your special discount:
                </p>

                {/* Coupon Code Box */}
                <div
                  style={{
                    background: "#F8FAFC",
                    border: "1.5px dashed #3B82F6",
                    borderRadius: "10px",
                    padding: "10px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "16px",
                  }}
                >
                  <div>
                    <span style={{ fontSize: "10.5px", color: "#64748B", textTransform: "uppercase", fontWeight: "700" }}>
                      Coupon Code
                    </span>
                    <h4 style={{ margin: "2px 0 0 0", color: "#1E40AF", fontWeight: "900", letterSpacing: "1px" }}>
                      {couponCode}
                    </h4>
                  </div>
                  <Button
                    variant={copied ? "success" : "outline-primary"}
                    size="sm"
                    onClick={handleCopyCoupon}
                    style={{ fontWeight: "700", borderRadius: "8px" }}
                  >
                    {copied ? <><FaCheck /> Copied</> : <><FaCopy /> Copy</>}
                  </Button>
                </div>

                <Button
                  onClick={handleWhatsAppRedirect}
                  style={{
                    width: "100%",
                    padding: "11px",
                    borderRadius: "8px",
                    background: "#25D366",
                    border: "none",
                    fontWeight: "700",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <FaWhatsapp size={18} />
                  <span>Chat on WhatsApp</span>
                </Button>

                <button
                  onClick={handleClose}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6B7280",
                    fontSize: "12px",
                    textDecoration: "underline",
                    cursor: "pointer",
                    marginTop: "6px",
                  }}
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default LeadCaptureModal;
